/**
 * TableContextMenu — Right-click context menu for table operations.
 *
 * Appears when the user right-clicks inside a `.finlytics-table` cell.
 * Provides: insert/delete rows & columns, delete table.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';

/* ── DOM helpers ───────────────────────────────────────── */

function closestCell(el) {
    return el?.closest?.('td, th') || null;
}
function closestRow(el) {
    return el?.closest?.('tr') || null;
}
function closestTable(el) {
    return el?.closest?.('table.finlytics-table') || null;
}

function cellIndex(td) {
    return td ? Array.from(td.parentElement.children).indexOf(td) : -1;
}

function insertRowAbove(tr) {
    const cols = tr.children.length;
    const row = tr.ownerDocument.createElement('tr');
    for (let i = 0; i < cols; i++) {
        const td = tr.ownerDocument.createElement('td');
        td.setAttribute('contenteditable', 'true');
        row.appendChild(td);
    }
    tr.parentElement.insertBefore(row, tr);
}

function insertRowBelow(tr) {
    const cols = tr.children.length;
    const row = tr.ownerDocument.createElement('tr');
    for (let i = 0; i < cols; i++) {
        const td = tr.ownerDocument.createElement('td');
        td.setAttribute('contenteditable', 'true');
        row.appendChild(td);
    }
    tr.parentElement.insertBefore(row, tr.nextSibling);
}

function deleteRow(tr) {
    const tbody = tr.closest('tbody');
    if (!tbody) return; // don't delete header row
    if (tbody.rows.length <= 1) {
        // last row — delete the entire table
        const tbl = closestTable(tr);
        tbl?.remove();
        return;
    }
    tr.remove();
}

function insertColumnLeft(table, colIdx) {
    for (const row of table.rows) {
        const cell = row.insertCell(colIdx);
        const tag = row.parentElement.tagName === 'THEAD' ? 'th' : 'td';
        if (tag === 'th') {
            const th = row.ownerDocument.createElement('th');
            th.setAttribute('contenteditable', 'true');
            th.textContent = 'Header';
            cell.replaceWith(th);
        } else {
            cell.setAttribute('contenteditable', 'true');
        }
    }
}

function insertColumnRight(table, colIdx) {
    insertColumnLeft(table, colIdx + 1);
}

function deleteColumn(table, colIdx) {
    const totalCols = table.rows[0]?.cells.length || 0;
    if (totalCols <= 1) {
        table.remove();
        return;
    }
    for (const row of table.rows) {
        if (row.cells[colIdx]) row.deleteCell(colIdx);
    }
}

/* ── Component ─────────────────────────────────────────── */

const MENU_ITEMS = [
    { id: 'row-above', label: 'Insert row above', section: 'row' },
    { id: 'row-below', label: 'Insert row below', section: 'row' },
    { id: 'delete-row', label: 'Delete row', section: 'row', danger: true },
    { id: 'sep-1', type: 'separator' },
    { id: 'col-left', label: 'Insert column left', section: 'col' },
    { id: 'col-right', label: 'Insert column right', section: 'col' },
    { id: 'delete-col', label: 'Delete column', section: 'col', danger: true },
    { id: 'sep-2', type: 'separator' },
    { id: 'delete-table', label: 'Delete table', section: 'table', danger: true },
];

export default function TableContextMenu({ editorRef, onContentChange }) {
    const [menu, setMenu] = useState(null); // { x, y, cell, row, table, colIdx }
    const menuRef = useRef(null);

    /* Open on right-click inside a table cell */
    const handleContextMenu = useCallback((e) => {
        const cell = closestCell(e.target);
        const table = closestTable(e.target);
        if (!cell || !table) {
            setMenu(null);
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        setMenu({
            x: e.clientX,
            y: e.clientY,
            cell,
            row: closestRow(cell),
            table,
            colIdx: cellIndex(cell),
            isHeader: cell.tagName === 'TH',
        });
    }, []);

    /* Close on outside click or Escape */
    useEffect(() => {
        if (!menu) return;
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(null);
        };
        const esc = (e) => { if (e.key === 'Escape') setMenu(null); };
        document.addEventListener('mousedown', close, true);
        document.addEventListener('keydown', esc, true);
        return () => {
            document.removeEventListener('mousedown', close, true);
            document.removeEventListener('keydown', esc, true);
        };
    }, [menu]);

    /* Attach contextmenu listener to editor */
    useEffect(() => {
        const el = editorRef?.current;
        if (!el) return;
        el.addEventListener('contextmenu', handleContextMenu);
        return () => el.removeEventListener('contextmenu', handleContextMenu);
    }, [editorRef, handleContextMenu]);

    /* Execute menu action */
    const handleAction = useCallback((id) => {
        if (!menu) return;
        const { row, table, colIdx } = menu;
        switch (id) {
            case 'row-above': insertRowAbove(row); break;
            case 'row-below': insertRowBelow(row); break;
            case 'delete-row': deleteRow(row); break;
            case 'col-left': insertColumnLeft(table, colIdx); break;
            case 'col-right': insertColumnRight(table, colIdx); break;
            case 'delete-col': deleteColumn(table, colIdx); break;
            case 'delete-table': table.remove(); break;
            default: break;
        }
        setMenu(null);
        // Notify editor content changed
        onContentChange?.();
    }, [menu, onContentChange]);

    if (!menu) return null;

    /* Clamp menu position so it doesn't go off-screen */
    const style = {
        position: 'fixed',
        left: Math.min(menu.x, window.innerWidth - 210),
        top: Math.min(menu.y, window.innerHeight - 300),
        zIndex: 10000,
    };

    return (
        <div className="tbl-ctx-menu" ref={menuRef} style={style}>
            {MENU_ITEMS.map((item) => {
                if (item.type === 'separator') return <div key={item.id} className="tbl-ctx-sep" />;
                /* Disable "delete row" on header rows */
                const disabled = (item.id === 'delete-row' && menu.isHeader);
                return (
                    <button
                        key={item.id}
                        type="button"
                        className={`tbl-ctx-item ${item.danger ? 'danger' : ''}`}
                        disabled={disabled}
                        onClick={() => handleAction(item.id)}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
}
